import React, { useEffect, useRef } from "react";
import CodeMirror from "codemirror";
import "codemirror/mode/javascript/javascript";
import "codemirror/theme/dracula.css";
import "codemirror/addon/edit/closetag";
import "codemirror/addon/edit/closebrackets";
import "codemirror/lib/codemirror.css";
import { ACTIONS } from "../Actions";

const userColors = {};
const palette = ["#ff4d4f", "#40a9ff", "#73d13d", "#ffa940", "#b37feb", "#36cfc9"];

function getColor(id) {
  if (!userColors[id]) {
    userColors[id] = palette[Math.floor(Math.random() * palette.length)];
  }
  return userColors[id];
}

function hexToRgba(hex, alpha) {
  const normalized = hex.replace("#", "");
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function serializeChangeChain(change) {
  const changes = [];
  for (let current = change; current; current = current.next) {
    changes.push({
      from: current.from,
      to: current.to,
      text: current.text,
    });
  }
  return changes;
}

function applyRemoteChanges(editor, changes) {
  editor.operation(() => {
    changes.forEach(({ from, to, text }) => {
      const value = Array.isArray(text) ? text.join("\n") : text || "";
      editor.replaceRange(value, from, to, "remote");
    });
  });
}

function Editor({ socketRef, roomId, onCodeChange, canEdit = true }) {
  const editorRef = useRef(null);
  const textareaRef = useRef(null);
  const cursorMarkers = useRef({});
  const selectionMarkers = useRef({});
  const isRemote = useRef(false);
  const lastCursorEmit = useRef(0);

  // INIT EDITOR
  useEffect(() => {
    if (!textareaRef.current) return;

    if (editorRef.current) {
      editorRef.current.toTextArea();
      editorRef.current = null;
    }

    const editor = CodeMirror.fromTextArea(textareaRef.current, {
      mode: "javascript",
      theme: "dracula",
      lineNumbers: true,
      autoCloseTags: true,
      autoCloseBrackets: true,
      readOnly: !canEdit,
    });

    editorRef.current = editor;
    editor.setSize(null, "100%");

    // Emit selection changes for collaborative highlighting.
    editor.on("cursorActivity", () => {
      if (!socketRef.current) return;
      const selection = editor.listSelections();
      socketRef.current.emit(ACTIONS.SELECTION_CHANGE, { roomId, selection });
    });

    // CODE CHANGE
    editor.on("change", (cm, change) => {
      if (!canEdit) return;
      if (isRemote.current || change.origin === "setValue" || change.origin === "remote") return;

      const code = cm.getValue();
      onCodeChange(code);
      const changes = serializeChangeChain(change);
      if (socketRef.current) {
        socketRef.current.emit(ACTIONS.CODE_CHANGE, { roomId, changes });
      }
    });

    // CURSOR MOVE
    editor.on("cursorActivity", () => {
      const now = Date.now();
      if (now - lastCursorEmit.current < 80) return;
      lastCursorEmit.current = now;

      const cursor = editor.getCursor();
      if (socketRef.current) {
        socketRef.current.emit(ACTIONS.CURSOR_MOVE, { roomId, cursor });
      }
    });

    return () => editor.toTextArea();
  }, [canEdit, onCodeChange, roomId, socketRef]);

  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.setOption("readOnly", !canEdit);
  }, [canEdit]);

  // RECEIVE CODE
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handler = ({ code, changes }) => {
      if (!editorRef.current) return;

      isRemote.current = true;
      if (Array.isArray(changes) && changes.length > 0) {
        applyRemoteChanges(editorRef.current, changes);
        onCodeChange(editorRef.current.getValue());
      } else if (typeof code === "string") {
        editorRef.current.setValue(code);
        onCodeChange(code);
      }
      isRemote.current = false;
    };

    socket.on(ACTIONS.CODE_CHANGE, handler);
    return () => socket.off(ACTIONS.CODE_CHANGE, handler);
  }, [onCodeChange, socketRef]);

  // RECEIVE CURSOR
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handler = ({ socketId, cursor, username }) => {
      if (!editorRef.current) return;

      const color = getColor(socketId);

      if (cursorMarkers.current[socketId]) {
        cursorMarkers.current[socketId].clear();
      }

      const cursorEl = document.createElement("span");
      cursorEl.style.borderLeft = `2px solid ${color}`;
      cursorEl.style.height = "1em";

      const label = document.createElement("div");
      label.innerText = username;
      label.style.position = "absolute";
      label.style.background = color;
      label.style.color = "white";
      label.style.fontSize = "10px";
      label.style.padding = "2px 4px";
      label.style.borderRadius = "4px";
      label.style.transform = "translateY(-14px)";
      label.style.whiteSpace = "nowrap";

      const wrapper = document.createElement("span");
      wrapper.style.position = "relative";
      wrapper.appendChild(label);
      wrapper.appendChild(cursorEl);

      cursorMarkers.current[socketId] = editorRef.current.setBookmark(cursor, {
        widget: wrapper,
      });
    };

    socket.on(ACTIONS.CURSOR_MOVE, handler);
    return () => socket.off(ACTIONS.CURSOR_MOVE, handler);
  }, [socketRef]);

  // RECEIVE SELECTION
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handler = ({ socketId, selection }) => {
      if (!editorRef.current || !selection) return;

      if (selectionMarkers.current[socketId]) {
        selectionMarkers.current[socketId].forEach((marker) => marker.clear());
      }

      const editor = editorRef.current;
      const color = getColor(socketId);
      const selectionColor = hexToRgba(color, 0.35);
      const markers = [];

      selection.forEach((sel) => {
        if (
          sel.anchor.line === sel.head.line &&
          sel.anchor.ch === sel.head.ch
        ) {
          return;
        }

        const from =
          CodeMirror.cmpPos(sel.anchor, sel.head) <= 0 ? sel.anchor : sel.head;
        const to =
          CodeMirror.cmpPos(sel.anchor, sel.head) <= 0 ? sel.head : sel.anchor;

        markers.push(
          editor.markText(from, to, {
            css: `background: ${selectionColor}; border-radius: 2px;`,
          })
        );
      });

      selectionMarkers.current[socketId] = markers;
    };

    socket.on(ACTIONS.SELECTION_CHANGE, handler);
    return () => socket.off(ACTIONS.SELECTION_CHANGE, handler);
  }, [socketRef]);

  // CLEAR REMOTE MARKERS ON DISCONNECT
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handler = ({ socketId }) => {
      if (cursorMarkers.current[socketId]) {
        cursorMarkers.current[socketId].clear();
        delete cursorMarkers.current[socketId];
      }

      if (selectionMarkers.current[socketId]) {
        selectionMarkers.current[socketId].forEach((marker) => marker.clear());
        delete selectionMarkers.current[socketId];
      }
    };

    socket.on(ACTIONS.DISCONNECTED, handler);
    return () => socket.off(ACTIONS.DISCONNECTED, handler);
  }, [socketRef]);

  return (
    <div style={{ height: "600px" }}>
      <textarea ref={textareaRef}></textarea>
    </div>
  );
}
export default Editor;
